/**
 * ADR-0240 Slice 2d — "Scan widget" page.
 *
 * v4 UX (2026-10-10) — founder feedback:
 *
 *   - the widget key is not interesting on its own; it already lives
 *     inside the snippet. Drop the "Your widget key" field. The ONE
 *     snippet block carries the standard copy-button pattern used
 *     elsewhere in the dashboard (install pages / Settings).
 *   - the status chip must reflect reality: "Not set up" → "Add your
 *     website" → "Waiting for first scan" → "Working · last scan {ago}".
 *     Derived from `widget === null`, `origins.length`, and
 *     `widget.last_scan_at`. The same chip appears in the portal rail
 *     via the Overview endpoint.
 *   - drop the CSP jargon in the main surface; add a plain
 *     "Troubleshooting" Collapsible with layperson copy ("If the box
 *     doesn't appear, your site may block outside styles — ask your
 *     developer to allow inline styles for this widget.").
 *   - clear three-step instructions.
 *   - "Add site" input: no normalisation while typing (fixes the
 *     "https: /vbout" one-slash drop a founder saw on `type="url"`);
 *     normalise only on blur / click, so the raw characters stay as
 *     typed. The Add button saves immediately — one primary action,
 *     one clear pattern.
 */

import type { ReactNode } from 'react';
import { AlertCircle, Check, Copy, Globe, Code2, Trash2 } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { useOptionalHttp } from '../../api/http-context.js';
import { useAgency } from '../../app/agency-context.js';
import {
  bulkRemoveWidgetLeads,
  getAgencyWidget,
  getAgencyWidgetLeads,
  provisionAgencyWidget,
  putAgencyWidgetOrigins,
  putAgencyWidgetSettings,
  removeWidgetLead,
  type AgencyWidgetLeadWire,
  type AgencyWidgetWire,
  type PutAgencyWidgetOriginsResult,
} from '../../api/agency-widget.js';
import { useC } from '../../lib/theme.js';
import { Button, Card, Collapsible, ConfirmDialog, NumLabel, Tooltip } from '../../components/atoms/index.js';
import { useIsEmbedded } from '../agency-portal/portal-shell-context.js';

const monoFont = "'JetBrains Mono', monospace";
const sansFont = "'Geist', sans-serif";
const serifFont = "'Instrument Serif', serif";

export type WidgetTheme = 'auto' | 'light' | 'dark';

function buildSnippet(public_key: string, theme: WidgetTheme = 'auto'): string {
  const theme_attr = theme === 'auto' ? '' : ` data-sill-theme="${theme}"`;
  return `<div data-sill-widget="${public_key}"${theme_attr}></div>
<script src="https://sill.so/widget/scan.js" async></script>`;
}

type LoadState =
  | { kind: 'loading' }
  | { kind: 'ok'; widget: AgencyWidgetWire | null }
  | { kind: 'error'; message: string };

type WidgetStatus =
  | { kind: 'not_set_up'; label: string; tone: 'muted' }
  | { kind: 'needs_origin'; label: string; tone: 'amber' }
  | { kind: 'waiting_first_scan'; label: string; tone: 'sky' }
  | { kind: 'working'; label: string; tone: 'lime' };

/** v4 (2026-10-10) — four-state status chip. One source of truth for
 *  this page AND the portal rail (via the Overview endpoint, when the
 *  widget tile needs to reflect "working / waiting"). */
export function deriveWidgetStatus(
  widget: AgencyWidgetWire | null,
  now_ms: number = Date.now(),
): WidgetStatus {
  if (widget === null) return { kind: 'not_set_up', label: 'Not set up', tone: 'muted' };
  if ((widget.origins ?? []).length === 0) {
    return { kind: 'needs_origin', label: 'Add your website', tone: 'amber' };
  }
  if (widget.last_scan_at === null) {
    return { kind: 'waiting_first_scan', label: 'Waiting for first scan', tone: 'sky' };
  }
  return {
    kind: 'working',
    label: `Working · last scan ${timeAgo(widget.last_scan_at, now_ms)}`,
    tone: 'lime',
  };
}

function humanizeOriginsResult(
  r: Exclude<PutAgencyWidgetOriginsResult, { kind: 'ok' }>,
): string {
  switch (r.kind) {
    case 'invalid_origin':
      return r.message;
    case 'duplicate_origin':
      return 'Each website may be listed only once.';
    case 'too_many_origins':
      return 'No more than 10 websites per agency.';
    case 'forbidden':
      return 'You do not have permission to edit this.';
    case 'error':
      return r.message;
  }
}

export function AgencyWidget(): JSX.Element {
  const C = useC();
  const http = useOptionalHttp();
  const { features, acting } = useAgency();
  const [state, setState] = useState<LoadState>({ kind: 'loading' });
  const [provisioning, setProvisioning] = useState(false);
  const [provisionError, setProvisionError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [theme, setTheme] = useState<WidgetTheme>('auto');
  const [leads, setLeads] = useState<readonly AgencyWidgetLeadWire[] | null>(null);
  const [leadsError, setLeadsError] = useState<string | null>(null);
  const [selectedLeads, setSelectedLeads] = useState<ReadonlySet<string>>(new Set());
  const [confirmDeleteLeads, setConfirmDeleteLeads] = useState<
    | null
    | { kind: 'single'; lead_id: string; domain: string }
    | { kind: 'bulk'; lead_ids: readonly string[] }
  >(null);
  const [deletingLeads, setDeletingLeads] = useState(false);
  const [leadsRowError, setLeadsRowError] = useState<string | null>(null);
  const [emailCapture, setEmailCapture] = useState(false);
  const [emailCaptureLoading, setEmailCaptureLoading] = useState(false);
  const [emailCaptureError, setEmailCaptureError] = useState<string | null>(null);

  const refresh = useCallback(async (): Promise<void> => {
    if (http === null) return;
    setState({ kind: 'loading' });
    const r = await getAgencyWidget(http);
    if (r.kind === 'ok') {
      setState({ kind: 'ok', widget: r.widget });
      if (r.widget !== null) setEmailCapture(r.widget.email_capture_enabled);
    } else if (r.kind === 'forbidden') {
      setState({ kind: 'error', message: 'Agency access is not available on this account.' });
    } else {
      setState({ kind: 'error', message: r.message });
    }
  }, [http]);

  const refreshLeads = useCallback(async (): Promise<void> => {
    if (http === null) return;
    const r = await getAgencyWidgetLeads(http, { limit: 50 });
    if (r.kind === 'ok') {
      setLeads(r.leads);
      setLeadsError(null);
    } else if (r.kind === 'forbidden') {
      setLeads(null);
      setLeadsError('Agency access is not available.');
    } else {
      setLeads(null);
      setLeadsError(r.message);
    }
  }, [http]);

  useEffect(() => {
    if (features === null) return;
    if (features.agency !== true) return;
    if (acting !== null) return;
    void refresh();
    void refreshLeads();
  }, [features, acting, refresh, refreshLeads]);

  const onProvision = async (): Promise<void> => {
    if (http === null || provisioning) return;
    setProvisioning(true);
    setProvisionError(null);
    const r = await provisionAgencyWidget(http);
    setProvisioning(false);
    if (r.kind === 'ok') {
      setState({ kind: 'ok', widget: r.widget });
      return;
    }
    if (r.kind === 'forbidden') {
      if (r.reason === 'role_insufficient') {
        setProvisionError('Only owners and admins can set up the scan widget.');
        return;
      }
      if (r.reason === 'agency_feature_disabled') {
        setProvisionError('The scan widget is not enabled on this account. Contact Sill.');
        return;
      }
      if (r.reason === 'acting_session_denied') {
        setProvisionError('Return to your home account to set up the scan widget.');
        return;
      }
      if (r.reason === 'bad_origin') {
        setProvisionError('This browser blocked the request. Try again from the Sill dashboard tab.');
        return;
      }
      setProvisionError('You do not have permission to set up the scan widget.');
      return;
    }
    setProvisionError(
      r.message.length > 0
        ? r.message
        : 'Could not create the widget. Try again, or contact Sill if it keeps failing.',
    );
  };

  const saveOrigins = useCallback(
    async (next: readonly string[]): Promise<{ ok: boolean; message?: string }> => {
      if (http === null) return { ok: false, message: 'Not connected.' };
      const r = await putAgencyWidgetOrigins(http, next);
      if (r.kind === 'ok') {
        await refresh();
        return { ok: true };
      }
      return { ok: false, message: humanizeOriginsResult(r) };
    },
    [http, refresh],
  );

  const onCopySnippet = async (public_key: string): Promise<void> => {
    try {
      await navigator.clipboard.writeText(buildSnippet(public_key, theme));
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* silent — snippet is on screen for manual copy */
    }
  };

  const snippet = useMemo(() => {
    if (state.kind !== 'ok' || state.widget === null) return '';
    return buildSnippet(state.widget.public_key, theme);
  }, [state, theme]);

  const toggleLeadSelected = (lead_id: string): void => {
    setSelectedLeads((prev) => {
      const next = new Set(prev);
      if (next.has(lead_id)) next.delete(lead_id);
      else next.add(lead_id);
      return next;
    });
  };

  const toggleSelectAllLeads = (all: boolean): void => {
    setSelectedLeads(all && leads !== null ? new Set(leads.map((l) => l.lead_id)) : new Set());
  };

  const confirmSingleLead = (lead: AgencyWidgetLeadWire): void => {
    setLeadsRowError(null);
    setConfirmDeleteLeads({ kind: 'single', lead_id: lead.lead_id, domain: lead.domain });
  };

  const confirmBulkLeads = (): void => {
    setLeadsRowError(null);
    const lead_ids = Array.from(selectedLeads);
    if (lead_ids.length === 0) return;
    setConfirmDeleteLeads({ kind: 'bulk', lead_ids });
  };

  const doDeleteLeads = async (): Promise<void> => {
    if (http === null || confirmDeleteLeads === null || deletingLeads) return;
    setDeletingLeads(true);
    setLeadsRowError(null);
    if (confirmDeleteLeads.kind === 'single') {
      const target = confirmDeleteLeads.lead_id;
      const r = await removeWidgetLead(http, target);
      setDeletingLeads(false);
      setConfirmDeleteLeads(null);
      if (r.kind === 'ok' || r.kind === 'not_found') {
        setSelectedLeads((prev) => { const n = new Set(prev); n.delete(target); return n; });
        await refreshLeads();
        return;
      }
      setLeadsRowError(r.kind === 'forbidden' ? 'You do not have permission to delete leads.' : r.message);
      return;
    }
    const r = await bulkRemoveWidgetLeads(http, confirmDeleteLeads.lead_ids);
    setDeletingLeads(false);
    setConfirmDeleteLeads(null);
    if (r.kind === 'ok') {
      setSelectedLeads(new Set());
      await refreshLeads();
      return;
    }
    if (r.kind === 'empty') {
      setLeadsRowError('Select at least one lead to delete.');
      return;
    }
    if (r.kind === 'too_many_rows') {
      setLeadsRowError(`Delete at most ${r.max} leads at a time.`);
      return;
    }
    setLeadsRowError(r.kind === 'forbidden' ? 'You do not have permission to delete leads.' : r.message);
  };

  // Guardrails ------------------------------------------------------
  if (features === null) {
    return <WidgetShell>{renderLoading(C.muted)}</WidgetShell>;
  }
  if (acting !== null) {
    return (
      <WidgetShell>
        <Card className="p-5 sm:p-6">
          <div style={{ fontFamily: sansFont, fontSize: 13, color: C.muted, lineHeight: 1.5 }}>
            Return to your home account to manage the scan widget.
          </div>
        </Card>
      </WidgetShell>
    );
  }
  if (features.agency !== true) {
    return (
      <WidgetShell>
        <Card className="p-5 sm:p-6">
          <div style={{ fontFamily: sansFont, fontSize: 14, color: C.text, lineHeight: 1.5, marginBottom: 8 }}>
            The scan widget is an agency add-on.
          </div>
          <div style={{ fontFamily: sansFont, fontSize: 13, color: C.muted, lineHeight: 1.5 }}>
            Contact Sill to enable the agency surface on this account.
          </div>
        </Card>
      </WidgetShell>
    );
  }
  if (state.kind === 'loading') {
    return <WidgetShell>{renderLoading(C.muted)}</WidgetShell>;
  }
  if (state.kind === 'error') {
    return (
      <WidgetShell>
        <Card className="p-5 sm:p-6">
          <div style={{ fontFamily: sansFont, fontSize: 13, color: C.muted }}>
            {state.message}
          </div>
        </Card>
      </WidgetShell>
    );
  }
  const widget = state.widget;
  const status = deriveWidgetStatus(widget);

  return (
    <WidgetShell>
      {widget === null ? (
        <Card className="p-5 sm:p-6">
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 10,
              flexWrap: 'wrap',
              marginBottom: 10,
            }}
          >
            <div style={{ fontFamily: sansFont, fontSize: 15, color: C.text, lineHeight: 1.4, fontWeight: 500 }}>
              Put a scan box on your own website.
            </div>
            <StatusChip C={C} status={status} />
          </div>
          <div style={{ fontFamily: sansFont, fontSize: 13, color: C.muted, lineHeight: 1.5, marginBottom: 16 }}>
            Visitors check how ready their site is for AI agents. Each scan
            becomes a lead you can follow up on. Three steps:
          </div>
          <ol
            style={{
              listStyle: 'none',
              padding: 0,
              margin: '0 0 16px 0',
              fontFamily: sansFont,
              fontSize: 13,
              color: C.text,
              lineHeight: 1.5,
            }}
          >
            {[
              'Create your widget.',
              'Add your website address.',
              'Paste the code we give you onto your site.',
            ].map((label, i) => (
              <li
                key={label}
                style={{
                  display: 'flex',
                  gap: 10,
                  padding: '6px 0',
                  borderTop: i === 0 ? 'none' : `1px solid ${C.borderSoft}`,
                }}
              >
                <span
                  aria-hidden
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 20,
                    height: 20,
                    borderRadius: 999,
                    background: C.surface2,
                    border: `1px solid ${C.border}`,
                    fontFamily: monoFont,
                    fontSize: 11,
                    color: C.muted,
                    flex: '0 0 auto',
                  }}
                >
                  {i + 1}
                </span>
                <span>{label}</span>
              </li>
            ))}
          </ol>
          {provisionError !== null ? (
            <div
              role="alert"
              data-testid="widget-provision-error"
              style={{
                marginBottom: 12,
                padding: '10px 12px',
                background: C.coralDim,
                border: `1px solid ${C.border}`,
                borderLeft: `3px solid ${C.coralFg}`,
                borderRadius: 3,
                fontFamily: sansFont,
                fontSize: 13,
                color: C.text,
                display: 'flex',
                alignItems: 'flex-start',
                gap: 8,
                lineHeight: 1.5,
              }}
            >
              <AlertCircle
                size={14}
                color={C.coralFg}
                style={{ flexShrink: 0, marginTop: 2 }}
                aria-hidden="true"
              />
              <span>{provisionError}</span>
            </div>
          ) : null}
          <Button
            variant="primary"
            size="md"
            onClick={() => void onProvision()}
            loading={provisioning}
            data-testid="widget-create-key"
          >
            {provisioning ? 'Creating widget…' : 'Create widget'}
          </Button>
        </Card>
      ) : (
        <>
          {/* STEP 1 — Add the websites that may run the widget. */}
          <WidgetOriginRowList
            C={C}
            origins={widget.origins}
            status={status}
            saveOrigins={saveOrigins}
          />

          {/* EMAIL CAPTURE — optional toggle between Step 1 and Step 2. */}
          <Card className="p-5" style={{ marginTop: 16 }} data-testid="widget-email-capture-card">
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16 }}>
              <div>
                <div style={{ fontFamily: monoFont, fontSize: 9, color: C.muted, letterSpacing: 2, textTransform: 'uppercase', marginBottom: 4 }}>
                  Email capture
                </div>
                <p style={{ fontFamily: sansFont, fontSize: 13, color: C.muted, lineHeight: 1.5, margin: 0 }}>
                  When on, visitors see basic results then enter their email to get the full report link.
                </p>
              </div>
              <button
                type="button"
                data-testid="widget-email-capture-toggle"
                disabled={emailCaptureLoading}
                onClick={async () => {
                  if (!http || !widget) return;
                  const next = !emailCapture;
                  setEmailCaptureLoading(true);
                  setEmailCaptureError(null);
                  const r = await putAgencyWidgetSettings(http, { email_capture_enabled: next });
                  setEmailCaptureLoading(false);
                  if (r.kind === 'ok') {
                    setEmailCapture(next);
                  } else {
                    setEmailCaptureError(
                      r.kind === 'forbidden'
                        ? 'You do not have permission to change this setting.'
                        : `Could not save — ${r.kind === 'error' ? r.message : 'unexpected response'}`,
                    );
                  }
                }}
                style={{
                  padding: '6px 14px',
                  fontFamily: monoFont,
                  fontSize: 11,
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                  border: `1px solid ${emailCapture ? C.limeFg : C.border}`,
                  borderRadius: 2,
                  background: emailCapture ? C.limeDim : 'transparent',
                  color: emailCapture ? C.limeFg : C.muted,
                  cursor: emailCaptureLoading ? 'default' : 'pointer',
                  opacity: emailCaptureLoading ? 0.5 : 1,
                  flexShrink: 0,
                }}
              >
                {emailCaptureLoading ? '…' : emailCapture ? 'On' : 'Off'}
              </button>
            </div>
            {emailCaptureError !== null && (
              <p style={{ fontFamily: sansFont, fontSize: 12, color: C.coralFg, margin: '8px 0 0' }}>
                {emailCaptureError}
              </p>
            )}
          </Card>

          {/* STEP 2 — Paste the snippet on your site. ONE block, ONE copy
              button, same pattern as the Settings/install pages. */}
          <Card className="p-5 sm:p-6" style={{ marginTop: 24 }} data-testid="widget-snippet-card">
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                marginBottom: 6,
              }}
            >
              <Code2 size={13} color={C.muted} aria-hidden />
              <div
                style={{
                  fontFamily: monoFont,
                  fontSize: 9,
                  color: C.muted,
                  letterSpacing: 2,
                  textTransform: 'uppercase',
                }}
              >
                Step 2 · Paste this code before &lt;/body&gt;
              </div>
            </div>
            <div style={{ fontFamily: sansFont, fontSize: 13, color: C.muted, lineHeight: 1.5, marginBottom: 12 }}>
              Add the code anywhere on the pages where you want the scan box
              to appear. One line of HTML and one script tag — no setup.
            </div>
            {/* Theme picker — updates the snippet inline so the agency
                copies exactly the attribute they want. */}
            <div
              style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}
              data-testid="widget-theme-picker"
            >
              <span style={{ fontFamily: sansFont, fontSize: 12, color: C.muted }}>
                Theme
              </span>
              {(['auto', 'light', 'dark'] as const).map((t) => (
                <button
                  key={t}
                  type="button"
                  onClick={() => { setTheme(t); setCopied(false); }}
                  data-testid={`widget-theme-${t}`}
                  style={{
                    padding: '3px 10px',
                    fontFamily: monoFont,
                    fontSize: 10,
                    letterSpacing: '0.08em',
                    textTransform: 'uppercase',
                    border: `1px solid ${theme === t ? C.limeFg : C.border}`,
                    borderRadius: 2,
                    background: theme === t ? C.limeDim : 'transparent',
                    color: theme === t ? C.limeFg : C.muted,
                    cursor: 'pointer',
                  }}
                >
                  {t}
                </button>
              ))}
              {theme !== 'auto' ? (
                <span style={{ fontFamily: sansFont, fontSize: 11, color: C.muted }}>
                  — forces {theme} mode regardless of visitor's OS setting
                </span>
              ) : (
                <span style={{ fontFamily: sansFont, fontSize: 11, color: C.muted }}>
                  — follows visitor's OS preference
                </span>
              )}
            </div>
            <div
              className="relative p-4"
              style={{
                background: C.surface2,
                border: `1px solid ${C.border}`,
                borderRadius: 2,
                position: 'relative',
              }}
            >
              <button
                type="button"
                onClick={() => void onCopySnippet(widget.public_key)}
                aria-label="Copy snippet"
                data-testid="widget-snippet-copy"
                style={{
                  position: 'absolute',
                  top: 10,
                  right: 10,
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  padding: '4px 10px',
                  background: C.bg,
                  border: `1px solid ${C.border}`,
                  fontFamily: monoFont,
                  fontSize: 10,
                  color: copied ? C.limeFg : C.muted,
                  borderRadius: 2,
                  cursor: 'pointer',
                }}
              >
                {copied ? <Check size={10} /> : <Copy size={10} />}
                {copied ? 'COPIED' : 'COPY'}
              </button>
              <pre
                data-testid="widget-snippet"
                style={{
                  fontFamily: monoFont,
                  fontSize: 11,
                  color: C.text,
                  lineHeight: 1.7,
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  paddingRight: 60,
                  margin: 0,
                }}
              >
                {snippet}
              </pre>
            </div>
            {/* Troubleshooting — plain words, collapsed by default so the
                primary 3-step flow stays short. */}
            <div style={{ marginTop: 12 }}>
              <Collapsible variant="inline" label="Troubleshooting">
                <p
                  style={{
                    fontFamily: sansFont,
                    fontSize: 12.5,
                    color: C.muted,
                    lineHeight: 1.6,
                    margin: 0,
                    maxWidth: 560,
                  }}
                  data-testid="widget-troubleshooting"
                >
                  If the box doesn't appear, your site may block outside
                  styles. Ask your developer to allow inline styles for this
                  widget, or add the snippet after your main content.
                </p>
              </Collapsible>
            </div>
          </Card>

          {/* STEP 3 — Recent leads. The empty state names the real condition
              (snippet copied but no scan yet). */}
          <Card className="p-5 sm:p-6" style={{ marginTop: 24 }}>
            <div
              style={{
                fontFamily: monoFont,
                fontSize: 9,
                color: C.muted,
                letterSpacing: 2,
                textTransform: 'uppercase',
                marginBottom: 12,
              }}
            >
              Recent widget leads
            </div>
            {/* ADR-0240 Slice 2d MAJOR 2 — a widget scan is signed only
                by (public_key, Origin); neither is a secret. Any party
                can POST a scan from curl once they know the key + a
                registered origin. Dashboard reminds the agency to
                verify prospects before emailing. */}
            <p
              data-testid="widget-leads-note"
              style={{
                fontFamily: sansFont,
                fontSize: 12,
                color: C.muted,
                lineHeight: 1.5,
                marginBottom: 12,
              }}
            >
              Leads come from anyone who submits your public key — verify a
              prospect before emailing them.
            </p>
            {leadsError !== null ? (
              <div style={{ fontFamily: sansFont, fontSize: 13, color: C.muted }}>
                {leadsError}
              </div>
            ) : leads === null ? (
              renderLoading(C.muted)
            ) : leads.length === 0 ? (
              <div style={{ fontFamily: sansFont, fontSize: 13, color: C.muted, lineHeight: 1.5 }}>
                No scans yet. Add the snippet to your site and share it with
                prospects.
              </div>
            ) : (
              <>
                {selectedLeads.size > 0 ? (
                  <div
                    data-testid="widget-leads-bulk-bar"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 10,
                      padding: '8px 10px',
                      marginBottom: 8,
                      background: C.surface2,
                      border: `1px solid ${C.border}`,
                      borderRadius: 2,
                      fontFamily: sansFont,
                      fontSize: 13,
                      color: C.text,
                    }}
                  >
                    <span>{selectedLeads.size} selected</span>
                    <div className="flex items-center gap-2">
                      <Button variant="secondary" size="sm" onClick={() => setSelectedLeads(new Set())}>
                        Clear
                      </Button>
                      <Button
                        variant="danger"
                        size="sm"
                        data-testid="widget-leads-bulk-delete"
                        icon={Trash2}
                        onClick={confirmBulkLeads}
                      >
                        Delete {selectedLeads.size}
                      </Button>
                    </div>
                  </div>
                ) : null}
                <div style={{ overflowX: 'auto', margin: '0 -2px' }}>
                  <table
                    data-testid="widget-leads-table"
                    style={{ width: '100%', minWidth: 480, fontFamily: sansFont, fontSize: 13, color: C.text, borderCollapse: 'collapse' }}
                  >
                    <thead>
                      <tr>
                        <th style={{ padding: '8px 6px' }}>
                          <input
                            type="checkbox"
                            aria-label="Select all leads"
                            checked={leads.length > 0 && leads.every((l) => selectedLeads.has(l.lead_id))}
                            onChange={(e) => toggleSelectAllLeads(e.target.checked)}
                          />
                        </th>
                        <th style={{ textAlign: 'left', padding: '8px 6px', color: C.muted, fontWeight: 400 }}>
                          Domain
                        </th>
                        <th style={{ textAlign: 'left', padding: '8px 6px', color: C.muted, fontWeight: 400 }}>
                          Source
                        </th>
                        <th style={{ textAlign: 'left', padding: '8px 6px', color: C.muted, fontWeight: 400 }}>
                          Score
                        </th>
                        <th style={{ textAlign: 'left', padding: '8px 6px', color: C.muted, fontWeight: 400 }}>
                          Report
                        </th>
                        <th style={{ textAlign: 'left', padding: '8px 6px', color: C.muted, fontWeight: 400 }}>
                          When
                        </th>
                        <th style={{ textAlign: 'left', padding: '8px 6px', color: C.muted, fontWeight: 400 }}>
                          Email
                        </th>
                        <th style={{ padding: '8px 6px' }} />
                      </tr>
                    </thead>
                    <tbody>
                      {leads.map((lead) => (
                        <tr key={lead.lead_id} style={{ borderTop: `1px solid ${C.borderSoft ?? C.border}` }}>
                          <td style={{ padding: '8px 6px' }}>
                            <input
                              type="checkbox"
                              aria-label={`Select ${lead.domain}`}
                              checked={selectedLeads.has(lead.lead_id)}
                              onChange={() => toggleLeadSelected(lead.lead_id)}
                            />
                          </td>
                          <td style={{ padding: '8px 6px', fontFamily: monoFont, fontSize: 12 }}>
                            {lead.domain}
                          </td>
                          <td style={{ padding: '8px 6px' }}>
                            {lead.client_signal === 'browser' ? (
                              <span style={{ color: C.muted }}>Browser</span>
                            ) : (
                              <Tooltip content="Submitted without browser-shape headers. Treat as unverified.">
                                <span
                                  data-testid="widget-lead-unverified-marker"
                                  style={{ color: '#FFB627' }}
                                >
                                  Unverified source
                                </span>
                              </Tooltip>
                            )}
                          </td>
                          <td style={{ padding: '8px 6px' }}>
                            {lead.signals_present === null || lead.signals_total === null
                              ? '—'
                              : `${lead.signals_present} / ${lead.signals_total}`}
                          </td>
                          <td style={{ padding: '8px 6px' }}>
                            {lead.report_url === null ? (
                              <span style={{ color: C.muted }}>—</span>
                            ) : (
                              <a
                                href={lead.report_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{ color: C.text, textDecoration: 'underline' }}
                              >
                                View
                              </a>
                            )}
                          </td>
                          <td style={{ padding: '8px 6px', color: C.muted }}>
                            {formatWhen(lead.created_at)}
                          </td>
                          <td style={{ padding: '8px 6px', fontFamily: monoFont, fontSize: 12 }}>
                            {lead.email !== null && lead.email !== undefined
                              ? lead.email
                              : <span style={{ color: C.muted }}>—</span>}
                          </td>
                          <td style={{ padding: '8px 6px' }}>
                            <button
                              type="button"
                              onClick={() => confirmSingleLead(lead)}
                              data-testid={`widget-lead-delete-${lead.lead_id}`}
                              aria-label={`Delete lead ${lead.domain}`}
                              style={{
                                background: 'transparent',
                                border: 'none',
                                color: C.coralFg,
                                cursor: 'pointer',
                                padding: 4,
                              }}
                            >
                              <Trash2 size={13} />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {leadsRowError !== null ? (
                  <div
                    data-testid="widget-leads-row-error"
                    role="alert"
                    style={{ marginTop: 10, fontFamily: sansFont, fontSize: 13, color: C.coralFg }}
                  >
                    {leadsRowError}
                  </div>
                ) : null}
              </>
            )}
          </Card>
          <ConfirmDialog
            open={confirmDeleteLeads !== null}
            title={
              confirmDeleteLeads === null
                ? 'Delete lead?'
                : confirmDeleteLeads.kind === 'single'
                  ? `Delete lead for ${confirmDeleteLeads.domain}?`
                  : `Delete ${confirmDeleteLeads.lead_ids.length} leads?`
            }
            body={
              confirmDeleteLeads === null ? null : (
                <>This removes the {confirmDeleteLeads.kind === 'bulk' ? 'selected leads' : 'lead'} from your list. It can't be undone.</>
              )
            }
            confirmLabel={deletingLeads ? 'Deleting…' : 'Delete'}
            confirmTone="coral"
            onConfirm={() => void doDeleteLeads()}
            onClose={() => { if (!deletingLeads) setConfirmDeleteLeads(null); }}
          />
        </>
      )}
    </WidgetShell>
  );
}

function renderLoading(muted: string): ReactNode {
  return (
    <Card className="p-5 sm:p-6">
      <div style={{ fontFamily: monoFont, fontSize: 11, color: muted, letterSpacing: 1 }}>
        Loading…
      </div>
    </Card>
  );
}

/** v4 (2026-10-10) — status chip. One pill, four states. The tone
 *  maps to the shared palette tokens (muted / amber / sky / lime).
 *  Exported for the portal rail to render the same chip beside the
 *  Widget tile. */
function StatusChip({
  C,
  status,
}: {
  readonly C: ReturnType<typeof useC>;
  readonly status: WidgetStatus;
}): JSX.Element {
  const color =
    status.tone === 'lime'
      ? C.limeFg
      : status.tone === 'amber'
        ? C.amberFg ?? '#FFB627'
        : status.tone === 'sky'
          ? C.skyFg ?? '#7FB8FF'
          : C.muted;
  return (
    <span
      data-testid="widget-status-chip"
      data-status={status.kind}
      style={{
        fontFamily: monoFont,
        fontSize: 11,
        letterSpacing: 1,
        color,
        textTransform: 'uppercase',
      }}
    >
      {`● ${status.label}`}
    </span>
  );
}

/**
 * Websites allowed to run the widget. v4 (2026-10-10):
 *
 *   - Each row has an Add site input + Remove on existing rows.
 *   - Typing is NEVER modified: `pending` holds the raw input (the
 *     old `type="url"` bug where typing `https://vbout` rendered
 *     `https: /vbout` came from `type="url"` auto-normalising inside
 *     some browsers — switch to `type="text"` + normalise on commit).
 *   - Add saves immediately via `saveOrigins`. One primary action.
 */
function WidgetOriginRowList({
  C,
  origins,
  status,
  saveOrigins,
}: {
  readonly C: ReturnType<typeof useC>;
  readonly origins: readonly string[];
  readonly status: WidgetStatus;
  readonly saveOrigins: (next: readonly string[]) => Promise<{ ok: boolean; message?: string }>;
}): JSX.Element {
  const [pending, setPending] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const normalise = (raw: string): string => {
    const trimmed = raw.trim();
    if (trimmed === '') return '';
    if (!/^https?:\/\//i.test(trimmed)) return `https://${trimmed}`;
    const forced = trimmed.replace(/^http:\/\//i, 'https://');
    // Strip anything after the host (paths/query/trailing slash) — the
    // server validator rejects those with `has_path`.
    try {
      const u = new URL(forced);
      return `${u.protocol}//${u.host}`.toLowerCase();
    } catch {
      return forced.toLowerCase();
    }
  };

  const canAdd = useMemo(() => {
    const n = normalise(pending);
    return n.length > 0 && !origins.includes(n) && !saving;
  }, [pending, origins, saving]);

  const onAdd = async (): Promise<void> => {
    const n = normalise(pending);
    if (n === '' || origins.includes(n)) return;
    setSaving(true);
    setError(null);
    const r = await saveOrigins([...origins, n]);
    setSaving(false);
    if (r.ok) {
      setPending('');
    } else {
      setError(r.message ?? 'Could not save. Try again.');
    }
  };

  const onRemove = async (origin: string): Promise<void> => {
    setSaving(true);
    setError(null);
    const next = origins.filter((o) => o !== origin);
    const r = await saveOrigins(next);
    setSaving(false);
    if (!r.ok) setError(r.message ?? 'Could not save. Try again.');
  };

  return (
    <Card className="p-5 sm:p-6" data-testid="widget-origins-card">
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 10,
          flexWrap: 'wrap',
          marginBottom: 6,
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
          }}
        >
          <Globe size={13} color={C.muted} aria-hidden />
          <div
            style={{
              fontFamily: monoFont,
              fontSize: 9,
              color: C.muted,
              letterSpacing: 2,
              textTransform: 'uppercase',
            }}
          >
            Step 1 · Add your websites
          </div>
        </div>
        <StatusChip C={C} status={status} />
      </div>
      <div style={{ fontFamily: sansFont, fontSize: 13, color: C.muted, lineHeight: 1.5, marginBottom: 12 }}>
        Only the sites you list can run your scan widget. HTTPS only. Up to 10.
      </div>
      {origins.length === 0 ? (
        <div
          style={{
            fontFamily: sansFont,
            fontSize: 13,
            color: C.muted,
            padding: '12px 0',
            marginBottom: 4,
          }}
        >
          No websites yet. Add one below to start.
        </div>
      ) : (
        <ul data-testid="widget-origins-list" style={{ listStyle: 'none', padding: 0, margin: 0 }}>
          {origins.map((origin) => (
            <li
              key={origin}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '8px 10px',
                border: `1px solid ${C.borderSoft}`,
                borderRadius: 2,
                marginBottom: 6,
                fontFamily: monoFont,
                fontSize: 12,
                fontVariantLigatures: 'none',
                color: C.text,
                gap: 10,
              }}
            >
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {origin}
              </span>
              <Button
                variant="ghost"
                size="sm"
                icon={Trash2}
                data-testid="widget-origin-remove"
                disabled={saving}
                onClick={() => void onRemove(origin)}
              >
                Remove
              </Button>
            </li>
          ))}
        </ul>
      )}
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (canAdd) void onAdd();
        }}
        style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 6 }}
      >
        <input
          // v4 (2026-10-10) — type=text, NOT type=url. The founder saw
          // `https://vbout` render as `https: /vbout` while typing on
          // the previous type=url input (some Chromium versions can
          // sanitise double-slash inside url-typed inputs). text keeps
          // raw characters; we normalise on submit only.
          type="text"
          data-testid="widget-origin-pending"
          placeholder="https://your-agency.example"
          value={pending}
          onChange={(e) => setPending(e.target.value)}
          spellCheck={false}
          autoCapitalize="off"
          autoCorrect="off"
          style={{
            flex: 1,
            minWidth: 0,
            fontFamily: monoFont,
            fontSize: 13,
            fontVariantLigatures: 'none',
            color: C.text,
            background: C.surface,
            border: `1px solid ${C.border}`,
            borderRadius: 3,
            padding: '7px 10px',
          }}
        />
        <Button
          variant="primary"
          size="md"
          type="submit"
          disabled={!canAdd}
          loading={saving && pending.length > 0}
          data-testid="widget-origin-add"
        >
          Add
        </Button>
      </form>
      {error !== null ? (
        <div
          data-testid="widget-origins-error"
          style={{ fontFamily: sansFont, fontSize: 13, color: C.coralFg, marginTop: 10 }}
        >
          {error}
        </div>
      ) : null}
    </Card>
  );
}

function formatWhen(iso: string): string {
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleString();
  } catch {
    return '—';
  }
}

/** v4 (2026-10-10) — compact relative time for the status chip
 *  ("3 min ago", "4 h ago", "2 d ago"). Exported for test coverage. */
export function timeAgo(iso: string, now_ms: number): string {
  try {
    const t = new Date(iso).getTime();
    if (Number.isNaN(t)) return 'recently';
    const diff_s = Math.max(1, Math.floor((now_ms - t) / 1000));
    if (diff_s < 60) return `${diff_s}s ago`;
    const diff_m = Math.floor(diff_s / 60);
    if (diff_m < 60) return `${diff_m} min ago`;
    const diff_h = Math.floor(diff_m / 60);
    if (diff_h < 48) return `${diff_h} h ago`;
    const diff_d = Math.floor(diff_h / 24);
    return `${diff_d} d ago`;
  } catch {
    return 'recently';
  }
}

function WidgetShell({ children }: { children: ReactNode }): JSX.Element {
  const C = useC();
  const embedded = useIsEmbedded();
  if (embedded) {
    return <>{children}</>;
  }
  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-10">
      <div className="mb-8">
        <NumLabel n={1}>Agency</NumLabel>
        <h1
          style={{
            fontFamily: serifFont,
            fontSize: 'clamp(32px, 5vw, 44px)',
            lineHeight: 1,
            color: C.text,
            letterSpacing: -1.5,
          }}
        >
          Scan widget.
        </h1>
      </div>
      {children}
    </div>
  );
}
